import { useNavigate, useLocation } from 'react-router-dom';
import { Home, PieChart, User, Building2, FileText, Receipt, LogOut } from 'lucide-react';

export default function DashboardLayout({ children, user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const isActive = (path) => {
    // Exact match for most paths
    if (location.pathname === path) {
      return true;
    }
    // Special case: Home should be active on both /dashboard/home and /dashboard (base)
    if (path === '/dashboard/home' && location.pathname === '/dashboard') {
      return false; // Don't highlight Home when on Portfolio (/dashboard)
    }
    // Special case: Portfolio is exactly /dashboard only
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Estates is active for any /investor paths
    if (path === '/investor/estates') {
      return location.pathname.startsWith('/investor');
    }
    return false;
  };

  const navigation = {
    general: [
      { name: 'Home', icon: Home, path: '/dashboard/home' },
      { name: 'Portfolio', icon: PieChart, path: '/dashboard' },
      { name: 'Profile', icon: User, path: '/profile' }
    ],
    products: [
      { name: 'Estates', icon: Building2, path: '/investor/estates' }
    ],
    more: [
      { name: 'Reports', icon: FileText, path: '/dashboard/reports' },
      { name: 'Transactions', icon: Receipt, path: '/dashboard/transactions' }
    ]
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 z-40 overflow-y-auto">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center">
                <span className="text-black font-bold text-lg">A</span>
              </div>
              <div>
                <h1 className="text-lg font-light tracking-tight text-white">ALTX</h1>
                <p className="text-xs text-white/40 font-mono">DASHBOARD</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-6">
            {/* General */}
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-3 px-4">General</p>
              <div className="space-y-1">
                {navigation.general.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30 text-[#4ADE80]'
                          : 'text-white/50 hover:text-white hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Products */}
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-3 px-4">Products</p>
              <div className="space-y-1">
                {navigation.products.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.name}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30 text-[#4ADE80]'
                          : 'text-white/50 hover:text-white hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* More */}
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-3 px-4">More</p>
              <div className="space-y-1">
                {navigation.more.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30 text-[#4ADE80]'
                          : 'text-white/50 hover:text-white hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Logout at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-white/50 hover:text-white hover:bg-white/5 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 ml-64">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}